/**
 * Auth layout — the prototype `.auth` shell: centred toddoo logo lockup over a
 * 420px glass card. Used by /login, /signup, check-inbox and dead-end pages.
 * Wraps everything in the `.tdo` brand scope and mounts the icon sprite once.
 * Server-safe.
 */
import type { ReactNode } from "react";
import { cx } from "@/components/ui/cx";
import { GlassCard } from "./glass-card";
import { BrandSprite } from "./icons";
import { BrandWordmark } from "./brand-wordmark";

export interface AuthLayoutProps {
  /** Card heading (e.g. "Sign in"). Rendered as an `<h1>`. */
  title: ReactNode;
  /** Optional one-line subtitle under the heading. */
  subtitle?: ReactNode;
  /** Optional content above the card (e.g. an invite banner). */
  banner?: ReactNode;
  /** Optional footer under the card (e.g. "New to Toddoo? Create an account"). */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function AuthLayout({
  title,
  subtitle,
  banner,
  footer,
  className,
  children,
}: AuthLayoutProps) {
  return (
    <div className={cx("tdo", className)}>
      <BrandSprite />
      <div className="tdo-auth">
        <div className="tdo-auth-logo">
          <BrandWordmark />
          <span
            aria-hidden="true"
            style={{
              width: "1px",
              height: "18px",
              background: "var(--tdo-border)",
            }}
          />
          <span className="tdo-appname">Floorplan Manager</span>
        </div>

        <GlassCard className="tdo-auth-card">
          {banner}
          <h1 className="tdo-h4">{title}</h1>
          {subtitle ? <p className="tdo-auth-sub">{subtitle}</p> : null}
          {children}
          {footer ? <div className="tdo-auth-foot">{footer}</div> : null}
        </GlassCard>
      </div>
    </div>
  );
}
